import { isProduction } from 'config';
import { useState, useEffect } from 'react';

export const ROOT_ID = 0;

export type Id = string | number;

export type Item = {
  id: Id;
  parentId: Id;
  name: string;
  invisible?: boolean;
  disabled?: boolean;
  state?: CheckboxState;
};

export enum CheckboxState {
  UNCHECKED,
  CHECKED
}

export type ParentsMap = Record<Id, Id[]>;
export type ItemsMap = Record<Id, Item>;
export type ToggleHandler = (id: Id, value: boolean) => void;
export type Checker = (id: Id) => boolean;

// Helper function to set state with callback.
// https://betterprogramming.pub/synchronous-state-in-react-using-hooks-dc77f43d8521
const useStateWithCallback = <T, F extends Function>(initialValue: T): [T, (v: T, cb: F) => unknown] => {
  const [value, setValue] = useState(initialValue);

  const setValueAndCallback = (newValue: T, callback: F) => {
    setValue(oldValue => {
      if (callback) {
        callback(newValue, oldValue);
      }
      return newValue;
    });
  };

  return [value, setValueAndCallback];
};

/**
 * @param initialData {item[]} - initial data set, as a flat array of items with id and parentId
 * @param onChangeCb {function} - callback to be called on every change of selected set of items.
 *  Receives selected items set (the ones with state = checked) as an argument.
 * @param filterQuery {string} - query (string) items name to be filtered against.
 * @param isAllToggled {boolean} - bool flag to indicate whether all elements are checked.
 *
 * Hook to process tree data in a way to be consumed by TreeLayout and expose a bunch of imperative methods for
 * data handling.
 *
 * Filtering and Toggling all operations are being performed on an initial stage (and all consequent filter query and
 * toggle flag changes). All other operations (toggling single item, toggling branch, etc.) are being performed by
 * imperative methods calling.
 *
 * @return {Object} resource - object containing API and data
 * @return {Object} resource.api - all exposed methods
 * @return {Object} resource.data - items map and parents map
 */

function useTreeData(
  initialData: Item[],
  onChangeCb: (items: Item[]) => void,
  filterQuery: string = '',
  isAllToggled: boolean = false
): {
  api: {
    getChildren: (id: Id) => Item[];
    hasAllChildrenSelected: (id: Id) => boolean;
    isTreeFiltered: () => boolean;
    isParent: (id: Id) => boolean;
    toggleBranch: (id: Id, value: boolean) => void;
    toggleAllChildren: (id: Id, value: boolean) => void;
    hasSomeDescendantSelected: (id: Id) => boolean;
    toggleItem: (id: Id, value: boolean) => void;
    getData: (id: Id) => Item;
    toggleAllTree: (checked: boolean) => void;
  };
  data: {
    itemsMap: ItemsMap;
    parentsMap: ParentsMap;
  };
} {
  const [parentsMap, setParentsMap] = useStateWithCallback<ParentsMap, typeof onChangeCb>({});
  const [itemsMap, setItemsMap] = useStateWithCallback<ItemsMap, typeof onChangeCb>({});

  useEffect(() => {
    // map of parent-child relations:
    // parentId: [childId, childId, ...]
    const parents: ParentsMap = {};

    // map of items to quick access
    // key is itemId
    const items: ItemsMap = {};

    initialData.forEach((item: Item) => {
      if (!item.parentId) {
        // usually root item(s) would have parentId null or undefined or '' - it will set the parentId to be 0
        item.parentId = 0;
      }

      if (!parents[item.parentId]) {
        parents[item.parentId] = [];
      }

      if (parents[item.parentId]) {
        parents[item.parentId].push(item.id);
      }

      // set 'invisible' and 'checked' properties based on current filterQuery and SelectAll values
      if (itemsMap[item.id]) {
        items[item.id] = {
          ...itemsMap[item.id],
          invisible: !itemsMap[item.id].name.toLowerCase().includes(filterQuery.toLowerCase()),
          state: isAllToggled ? 1 : itemsMap[item.id].state ? itemsMap[item.id].state : CheckboxState.UNCHECKED,
          disabled: isAllToggled
        };
      } else {
        items[item.id] = {
          ...item,
          invisible: !item.name.toLowerCase().includes(filterQuery.toLowerCase()),
          state: isAllToggled ? 1 : item.state ? item.state : CheckboxState.UNCHECKED,
          disabled: isAllToggled
        };
      }
    });

    setParentsMap(parents, () => {});
    setItemsMap(items, () => {
      if (isAllToggled) {
        onChangeCb(filterSelected(items));
      }
    });
  }, [initialData, filterQuery, isAllToggled]);

  const isTreeFiltered = () => filterQuery !== '';

  // get all children of item by its id
  const getChildren = (id: Id): Item[] => {
    const childrenIds = parentsMap[id] || [];

    return childrenIds.reduce((acc: Item[], nextId: Id) => {
      return [...acc, itemsMap[nextId]];
    }, []);
  };

  // check if item has children
  const isParent: Checker = id => {
    return !!(parentsMap[id] && parentsMap[id].length > 0);
  };

  const toggleAllTree = (checked: boolean) => {
    const payload = Object.keys(itemsMap).reduce((acc: ItemsMap, key: Id) => {
      acc[key] = { ...itemsMap[key], state: checked ? CheckboxState.CHECKED : CheckboxState.UNCHECKED } as Item;
      return acc;
    }, {});

    setItemsMap(payload, () => onChangeCb(filterSelected(payload)));
  };

  // return item data
  const getData = (id: Id) => itemsMap[id];

  // change item state
  const toggleItem: ToggleHandler = (id, value) => {
    const item = { ...itemsMap[id], state: value ? 1 : 0 };
    const payload = { ...itemsMap, [id]: item };

    setItemsMap(payload, () => onChangeCb(filterSelected(payload)));
  };

  // change state of all item's children
  const toggleAllChildren: ToggleHandler = (id, value) => {
    const children = getChildren(id);
    const update = children.reduce((acc: ItemsMap, child: Item) => {
      acc[child.id] = { ...child, state: value ? CheckboxState.CHECKED : CheckboxState.UNCHECKED };
      return acc;
    }, {});

    const payload = { ...itemsMap, ...update };
    setItemsMap(payload, () => onChangeCb(filterSelected(payload)));
  };

  // toggles parent + all descendants by parent id
  const toggleBranch: ToggleHandler = (id, value) => {
    const updatedDescendants: ItemsMap = {};

    // recursively go through all descendants and toggle them
    function toggleItemAndChildren(id: Id, value: boolean) {
      const childrenIds = parentsMap[id] || [];
      const branchData = itemsMap[id];
      updatedDescendants[id] = { ...branchData, state: value ? CheckboxState.CHECKED : CheckboxState.UNCHECKED };
      if (childrenIds.length > 0) {
        childrenIds.forEach(id => toggleItemAndChildren(id, value));
      }
    }

    toggleItemAndChildren(id, value);

    const payload = { ...itemsMap, ...updatedDescendants };
    setItemsMap(payload, () => onChangeCb(filterSelected(payload)));
  };

  // check if item has at least one descendant checked
  const hasSomeDescendantSelected: Checker = id => {
    let checkedChildrenCount = 0;

    // recursive fn to count checked children
    function checkChildrenState(parentId: Id) {
      const childrenIds = parentsMap[parentId] || [];

      if (itemsMap[parentId].state === CheckboxState.CHECKED) {
        checkedChildrenCount = checkedChildrenCount + 1;
      }

      if (childrenIds.length > 0) {
        childrenIds.forEach(checkChildrenState);
      }
    }

    checkChildrenState(id);

    return checkedChildrenCount > 0;
  };

  // check if item has all children checked
  const hasAllChildrenSelected: Checker = id => {
    const childrenIds = parentsMap[id] || [];

    return childrenIds.every(id => itemsMap[id].state === CheckboxState.CHECKED);
  };

  // get all item with checked state
  const filterSelected = (items: ItemsMap) => {
    const result = [];

    for (let id in items) {
      if (items[id].state === CheckboxState.CHECKED) {
        result.push(items[id]);
      }
    }

    return result;
  };

  const api = {
    getChildren,
    isParent,
    getData,
    toggleItem,
    hasSomeDescendantSelected,
    hasAllChildrenSelected,
    toggleAllChildren,
    toggleBranch,
    isTreeFiltered,
    toggleAllTree
  };
  const data = {
    parentsMap,
    itemsMap
  };

  return {
    api,
    data
  };
}

export default useTreeData;
